import { apiFetch } from "../utils/api";
import { idToken } from "./authService";

export interface IntegrationException {
  id: number;
  message_type: string;
  company_id: string;
  error_message: string;
  message_content: string;
  trace: string;
  processed_at: string;
  created_at: string;
  updated_at: string;
}

async function token(): Promise<string> {
  return await idToken();
}

export interface IntegrationErrorsPage {
  items: IntegrationException[];
  meta: { total: number; page: number; per_page: number; total_pages: number };
}

export async function fetchIntegrationErrors(page = 1, perPage = 10): Promise<IntegrationErrorsPage> {
  const t = await token();
  return apiFetch(t, `/api/integration/errors?page=${page}&per_page=${perPage}`);
}

export async function replayException(id: number, messageContent?: string): Promise<void> {
  const t = await token();
  await apiFetch(t, `/api/integration/${id}/replay`, {
    method: "POST",
    body: JSON.stringify({ message_content: messageContent }),
  });
}

export async function deleteException(id: number): Promise<void> {
  const t = await token();
  await apiFetch(t, `/api/integration/errors/${id}`, { method: "DELETE" });
}

export interface OutboundIntegrationFailure {
  id: number;
  created_at: string;
  updated_at: string;
  target_url: string | null;
  http_response_code: number | string | null;
  message_request_body: string | null;
  response_body: string | null;
  stack_trace: string | null;
}

export async function fetchOutboundFailures(): Promise<OutboundIntegrationFailure[]> {
  const t = await token();
  const data = await apiFetch(t, "/api/integration/outbound_failures");
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export interface FailedExecution {
  job_id: number;
  error: string;
  created_at: string;
}

export interface JobStatus {
  pending_jobs: number;
  failed_jobs: number;
  failed_executions: FailedExecution[];
}

export async function fetchJobStatus(): Promise<JobStatus> {
  const t = await token();
  const res = await apiFetch(t, "/api/integration/job_status");
  return res.data;
}

export interface ActiveSession {
  session_id: string;
  user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  enabled: boolean | null;
}

export async function fetchActiveSessions(): Promise<ActiveSession[]> {
  const t = await token();
  const res = await apiFetch(t, "/api/integration/active_sessions");
  if (Array.isArray(res?.data?.active_sessions)) return res.data.active_sessions;
  return [];
}

export async function invalidateActiveSession(sessionId: string): Promise<void> {
  const t = await token();
  await apiFetch(t, `/api/integration/active_sessions/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
  });
}

export async function logoutAllActiveSessions(): Promise<void> {
  const t = await token();
  await apiFetch(t, "/api/integration/active_sessions", {
    method: "DELETE",
  });
}
